<template>
  <Section
    v-if="form.campaign.useAdvancedSetting"
    @back="$emit('back')"
    title="高度な設定"
    tip-message="高度な設定に戻る"
  >
    <v-layout v-if="!advancedSetting.disabledDevices">
      <v-flex xs3>
        <v-subheader>配信デバイス</v-subheader>
      </v-flex>
      <v-flex xs9 align-self-center>
        <template v-if="advancedSetting.deliveryDevices">
          <v-chip
            v-for="device in advancedSetting.deliveryDevices"
            :key="device"
            small
            outline
            color="blue"
          >
            <AppDevice :value="device" />
          </v-chip>
        </template>
        <span v-else>なし</span>
      </v-flex>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledInventories">
      <v-flex xs3>
        <v-subheader>配信面</v-subheader>
      </v-flex>
      <v-flex xs9 align-self-center>
        <template v-if="advancedSetting.deliveryInventories">
          <v-chip
            v-for="inventory in deliveryInventories"
            :key="inventory"
            small
            outline
            color="blue"
          >
            <AppInventory :value="inventory" />
          </v-chip>
        </template>
        <span v-else>なし</span>
      </v-flex>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledInventoryListTargeting">
      <v-flex xs3>
        <v-subheader>配信先設定</v-subheader>
      </v-flex>
      <v-flex>
        <div v-if="whiteListItems.length > 0" class="inventory-lists">
          <h4 class="pt-2 subheading grey--text text--darken-2">
            ホワイトリスト
          </h4>
          <div>
            <div>必須設定</div>
            <div>
              <v-chip
                v-for="(item, index) in whiteListItems"
                :key="index"
                outline
                >{{ item.name }}</v-chip
              >
            </div>
          </div>
        </div>
        <div class="inventory-lists">
          <h4 class="pt-2 subheading grey--text text--darken-2">
            ブラックリスト
          </h4>
          <div v-if="blackListRequiredItems.length > 0">
            <div>必須設定</div>
            <div>
              <v-chip
                v-for="(item, index) in blackListRequiredItems"
                :key="index"
                outline
                >{{ item.name }}</v-chip
              >
            </div>
          </div>
          <div>
            <div>任意設定</div>
            <div>
              <v-chip
                v-for="(item, index) in blackListOptionalItems"
                :key="index"
                outline
                >{{ item.name }}</v-chip
              >
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledOnlyJapaneseDevice">
      <v-flex xs3>
        <v-subheader>端末言語</v-subheader>
      </v-flex>
      <v-flex xs9 align-self-center>
        <span v-if="advancedSetting.onlyJapaneseDevice">
          日本語に限定する
        </span>
        <span v-else>限定しない</span>
      </v-flex>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledIpAreaSetting">
      <v-flex xs3>
        <v-subheader>アクセス元地域</v-subheader>
      </v-flex>
      <v-flex align-self-center>
        <span v-if="isIpAreaWholeCountry">日本全域</span>
        <template v-if="!isIpAreaWholeCountry">
          <v-chip
            v-for="id in advancedSetting.ipAreaSetting.ipAreaIds"
            :key="id"
            small
            outline
            color="blue"
            ><RegionName :form="form" :id="id" />
          </v-chip>
        </template>
      </v-flex>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledDemographicSetting" column>
      <v-subheader>ユーザー属性設定</v-subheader>
      <v-layout pl-4>
        <v-flex xs3>
          <v-subheader>性別</v-subheader>
        </v-flex>
        <v-flex xs9 align-self-center>
          <v-chip v-if="demographic.sex" small outline color="blue">
            <AppSex :value="demographic.sex" />
          </v-chip>
          <span v-else>全て</span>
        </v-flex>
      </v-layout>
      <v-layout pl-4>
        <v-flex xs3>
          <v-subheader class="subtitle-2">年代</v-subheader>
        </v-flex>
        <v-flex xs9 align-self-center>
          <template v-if="demographic.age">
            <v-chip
              v-for="generation in demographic.age"
              :key="generation"
              small
              outline
              color="blue"
            >
              <AppDemographicAge :value="generation" />
            </v-chip>
          </template>
          <span v-else>全て</span>
        </v-flex>
      </v-layout>
      <v-layout pl-4>
        <v-flex xs3>
          <v-subheader class="subtitle-2">婚姻関係</v-subheader>
        </v-flex>
        <v-flex xs9 align-self-center>
          <v-chip v-if="demographic.marriage" small outline color="blue">
            <AppMarriage :value="demographic.marriage" />
          </v-chip>
          <span v-else>全て</span>
        </v-flex>
      </v-layout>
      <v-layout v-if="!demographic.disabledPrefecture" pl-4>
        <v-flex xs3>
          <v-subheader class="subtitle-2">都道府県居住地</v-subheader>
        </v-flex>
        <v-flex v-if="demographic.prefecture" xs9 align-self-center>
          <v-chip
            v-for="prefId in demographic.prefecture"
            :key="prefId"
            small
            outline
            color="blue"
          >
            <AppPrefecture :id="prefId" />
          </v-chip>
        </v-flex>
        <v-flex v-else xs9 align-self-center>
          <span>全て</span>
        </v-flex>
      </v-layout>
    </v-layout>
    <v-layout v-if="!advancedSetting.disabledWeeklyDeliverySchedule" column>
      <v-subheader>配信曜日時間帯設定</v-subheader>
      <v-layout pl-4 wrap>
        <template v-for="(schedules, weekday) in deliverySchedulesByWeekday">
          <v-flex :key="`${weekday}-name`" xs1>
            <v-subheader class="align-start">
              <AppWeekday :value="weekday" />
            </v-subheader>
          </v-flex>
          <v-flex :key="`${weekday}-value`" xs11 mb-2>
            <template v-if="schedules.length > 0">
              <v-layout
                v-for="(schedule, index) in schedules"
                :key="`schedule-${index}`"
                column
                mb-1
              >
                {{ schedule.startHour }}:00 〜 {{ schedule.endHour }}:59
              </v-layout>
            </template>
            <template v-else>配信停止</template>
          </v-flex>
        </template>
      </v-layout>
    </v-layout>
  </Section>
</template>

<script>
import Section from './CampaignModalPageConfirmSection.vue'
import RegionName from './CampaignModalIpAreaRegionName'
import { DeliveryInventories } from '@/models/CampaignOperation/Constants'

export default {
  name: 'CampaignModalPageConfirmAdvanced',
  components: { Section, RegionName },
  props: {
    form: {
      type: Object,
      default: () => {},
    },
    advertiser: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: true,
    },
    inventoryLists: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    advancedSetting() {
      return this.form.campaign.advancedSetting
    },
    deliverySchedulesByWeekday() {
      const reducer = (acc, schedule) => {
        acc[schedule.day].push(schedule)
        return acc
      }
      return this.advancedSetting.weeklyDeliverySchedule.reduce(reducer, {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      })
    },
    whiteListItems() {
      return this.form.inventoryRequiredLists.whiteListItems
    },
    blackListRequiredItems() {
      return this.form.inventoryRequiredLists.blackListItems
    },
    blackListOptionalItems() {
      return this.form.inventoryOptionalLists.blackListItems.filter(item =>
        this.form.selectedBlackInventoryListOptionalIds.includes(item.id),
      )
    },
    demographic() {
      return this.advancedSetting.demographicSetting
    },
    isIpAreaWholeCountry() {
      return this.advancedSetting.ipAreaSetting.isIpAreaWholeCountry
    },
    deliveryInventories() {
      if (!this.advancedSetting.isSelectedWebInventory) {
        return this.advancedSetting.deliveryInventories
      }
      const inventories = this.advancedSetting.deliveryInventories.filter(
        inventory =>
          inventory !== DeliveryInventories.WEB &&
          inventory !== DeliveryInventories.MOBILE_WEB,
      )
      return [DeliveryInventories.WEB, ...inventories]
    },
  },
  created() {
    this.fetchInventoryRequiredLists()
    this.fetchInventoryOptionalLists()
    this.initSelectedBlackInventoryListIds()
  },
  methods: {
    fetchInventoryRequiredLists() {
      this.form.fetchInventoryRequiredLists()
    },
    fetchInventoryOptionalLists() {
      this.form.fetchInventoryOptionalLists()
    },
    initSelectedBlackInventoryListIds() {
      this.form.initInventoryList()
    },
  },
}
</script>

<style></style>
